/**
 * Friendships Vibe — Coaching Service
 *
 * Calls the DDD Knowledge Hub coaching endpoints (askCoach + profile/session
 * lifecycle). Adapted from the adult coaching pattern with:
 *  - 90-second client-side cooldown between messages
 *  - No Turnstile (Vibe apps don't have it configured yet)
 *  - No authHubApp (uses app directly)
 *
 * Australian English throughout.
 */

import { getFunctions, httpsCallableFromURL } from 'firebase/functions';
import { app } from './config/firebase';
import { cleanUserContext } from '../utils/companionContext';

const APP_ID = 'friendships-vibe';
const BASE_URL = 'https://australia-southeast1-ddd-knowledge-hub.cloudfunctions.net';
const COOLDOWN_MS = 90_000; // 90-second minimum between messages

const URLS = {
  askCoach: `${BASE_URL}/askCoach`,
  getProfile: `${BASE_URL}/getCoachingProfile`,
  updateProfile: `${BASE_URL}/updateCoachingProfile`,
  deleteProfile: `${BASE_URL}/deleteCoachingProfile`,
  exportData: `${BASE_URL}/exportCoachingData`,
  startSession: `${BASE_URL}/startCoachingSession`,
  endSession: `${BASE_URL}/endCoachingSession`,
  forgetSession: `${BASE_URL}/forgetCoachingSession`
};

function getCallable(url) {
  if (!app) return null;
  const fns = getFunctions(app, 'australia-southeast1');
  return httpsCallableFromURL(fns, url);
}

function mapError(error) {
  const code = error?.code;
  const details = error?.customData || error?.details || {};
  if (code === 'functions/unauthenticated') {
    return { success: false, error: 'signin-required', response: "You'll need to sign in to use coaching." };
  }
  if (code === 'functions/failed-precondition') {
    return { success: false, error: 'consent-required', response: 'Coaching consent is required before we can begin.' };
  }
  if (code === 'functions/resource-exhausted') {
    return {
      success: false,
      error: 'cap-exceeded',
      dailyCapExceeded: !!details.dailyCapExceeded,
      tier: details.tier,
      dailyLimit: details.dailyLimit,
      upgradeUrl: details.upgradeUrl,
      response: details.tier === 'free'
        ? "You've used your 5 free coaching messages today. Come back tomorrow, or upgrade for more."
        : "You've reached today's coaching limit. It resets at midnight."
    };
  }
  return { success: false, error: error?.message || 'unknown', response: "I'm having trouble connecting right now. Please try again in a moment." };
}

class CoachingService {
  constructor() {
    this.conversationHistory = [];
    this.sessionId = null;
    this.lastMessageAt = 0;
  }

  resetSession() {
    this.conversationHistory = [];
    this.sessionId = null;
  }

  /** Returns ms remaining in cooldown, or 0 if ready */
  getCooldownRemaining() {
    const elapsed = Date.now() - this.lastMessageAt;
    return Math.max(0, COOLDOWN_MS - elapsed);
  }

  async getProfile() {
    const callable = getCallable(URLS.getProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable({})).data; }
    catch (e) { return mapError(e); }
  }

  async grantConsent({ personalContext = {}, dataSharingScope = 'app-only' } = {}) {
    const callable = getCallable(URLS.updateProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      return (await callable({
        consent: { granted: true, retentionAccepted: true, dataSharingScope },
        personalContext
      })).data;
    } catch (e) { return mapError(e); }
  }

  async updateProfile(patch) {
    const callable = getCallable(URLS.updateProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable(patch)).data; }
    catch (e) { return mapError(e); }
  }

  async deleteAll() {
    const callable = getCallable(URLS.deleteProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      const res = (await callable({ appId: APP_ID })).data;
      this.resetSession();
      return res;
    } catch (e) { return mapError(e); }
  }

  async exportData() {
    const callable = getCallable(URLS.exportData);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable({})).data; }
    catch (e) { return mapError(e); }
  }

  async startSession({ topic = null, sessionType = 'exploration' } = {}) {
    const callable = getCallable(URLS.startSession);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      const res = (await callable({ appId: APP_ID, topic, sessionType })).data;
      if (res?.sessionId) this.sessionId = res.sessionId;
      this.conversationHistory = [];
      return res;
    } catch (e) { return mapError(e); }
  }

  async endSession({ rating = null } = {}) {
    if (!this.sessionId) return { success: true };
    const callable = getCallable(URLS.endSession);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      const res = (await callable({
        sessionId: this.sessionId,
        appId: APP_ID,
        rating,
        transcript: this.conversationHistory
      })).data;
      this.resetSession();
      return res;
    } catch (e) { return mapError(e); }
  }

  /**
   * @param {string} question
   * @param {Object} [opts]
   * @param {Object} [opts.userContext]
   * @param {string|null} [opts.characterId] - Hub-namespaced guide id
   *   (`fv_alex`). The Coaching page passes the guide the user chose at
   *   onboarding so both doors to the coach speak with the same voice; the
   *   Hub validates it against this app's own characters and falls back to
   *   the app default if it is unknown. Omitted = app default.
   */
  async askCoach(question, { userContext = {}, characterId = null } = {}) {
    // Client-side cooldown enforcement
    const remaining = this.getCooldownRemaining();
    if (remaining > 0) {
      return {
        success: false,
        error: 'cooldown',
        cooldownRemaining: remaining,
        response: `Take a moment to think about that. You can send another message in ${Math.ceil(remaining / 1000)} seconds.`
      };
    }

    const callable = getCallable(URLS.askCoach);
    if (!callable) return { success: false, error: 'not-ready', response: "Please sign in to use coaching." };
    try {
      const res = (await callable({
        question,
        appId: APP_ID,
        ...(characterId ? { characterId } : {}),
        conversationHistory: this.conversationHistory,
        sessionId: this.sessionId,
        userContext: cleanUserContext(userContext)
      })).data;
      if (res.success) {
        this.lastMessageAt = Date.now();
        if (res.sessionId) this.sessionId = res.sessionId;
        this.conversationHistory.push(
          { role: 'user', content: question },
          { role: 'assistant', content: res.response }
        );
        if (this.conversationHistory.length > 30) {
          this.conversationHistory = this.conversationHistory.slice(-30);
        }
      }
      return res;
    } catch (e) { return mapError(e); }
  }

  async forgetSession(sessionId) {
    const callable = getCallable(URLS.forgetSession);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable({ sessionId })).data; }
    catch (e) { return mapError(e); }
  }
}

const coachingService = new CoachingService();
export default coachingService;
export { CoachingService };
